import * as api from "../client";

const BASE = "/v1/profiles/audit";

/**
 * The kinds of entity a profile-change audit entry can reference. Mirrors the
 * profiles-api `entityType` values. Used for *outgoing* filters and panel props;
 * the incoming `AuditEntry.entityType` stays a plain string so unknown values
 * still render (we humanize the fallback in the UI).
 */
export type AuditEntityType =
  | "firm_profile"
  | "firm_logo"
  | "lawyer_profile"
  | "article"
  | "firm_tab"
  | "team_page"
  | "user_guide";

export type AuditAction = "created" | "updated" | "published" | "unpublished" | "deleted";

/** Known actor kinds. The wire value is a plain string — see `AuditActor.type`. */
export type AuditActorType = "user" | "system";

export type AuditActor = {
  id: string | null;
  email: string | null;
  /**
   * One of {@link AuditActorType} in practice, but nullable on the wire:
   * profiles-api stores null when a write arrives without `X-Actor-*` headers.
   */
  type: string | null;
  /** Originating BFF, e.g. "account-bff" / "staff-bff". Used to badge the portal. */
  source: string | null;
  /** True when a staff member was impersonating an account user. */
  isImpersonating: boolean;
};

export type AuditEntry = {
  id: string;
  /**
   * One of {@link AuditEntityType} in practice, but kept a plain string to match
   * the API response schema — a newly added entity type must not break the build.
   */
  entityType: string;
  entityId: string;
  /** e.g. firm profileType, lawyer profession, or firm-tab kind ("awards"). */
  entitySubType: string | null;
  /** Human name captured at write time (firm name / lawyer name / article title). */
  entityLabel: string | null;
  /**
   * Address-book company UUID the changed entity belongs to (null for
   * user_guide and any entity with no resolvable company). Used to badge the
   * originating firm in the actor cell.
   */
  companyId: string | null;
  action: AuditAction;
  /** RAW field keys, e.g. ["about","website","status","staffing"]. */
  changedFields: string[];
  actor: AuditActor;
  createdAt: string;
};

export type AuditListResponse = {
  items: AuditEntry[];
  total: number;
  limit: number;
  offset: number;
};

export type AuditFilters = {
  entityType?: AuditEntityType | string;
  entityId?: string;
  /** Actor identifier filter (profiles-api `actorId`). */
  actorId?: string;
  action?: AuditAction | string;
  /** ISO8601 lower bound (inclusive). */
  from?: string;
  /** ISO8601 upper bound (inclusive). */
  to?: string;
  limit?: number;
  offset?: number;
};

export const auditService = {
  async list(filters: AuditFilters = {}): Promise<AuditListResponse> {
    const params = new URLSearchParams();
    if (filters.entityType) params.append("entityType", filters.entityType);
    if (filters.entityId) params.append("entityId", filters.entityId);
    if (filters.actorId) params.append("actorId", filters.actorId);
    if (filters.action) params.append("action", filters.action);
    if (filters.from) params.append("from", filters.from);
    if (filters.to) params.append("to", filters.to);
    if (filters.limit !== undefined) params.append("limit", String(filters.limit));
    if (filters.offset !== undefined) params.append("offset", String(filters.offset));
    const query = params.toString() ? `?${params}` : "";
    const raw = await api.get<Partial<AuditListResponse>>(`${BASE}${query}`);
    return {
      items: raw.items ?? [],
      total: raw.total ?? 0,
      limit: raw.limit ?? filters.limit ?? 50,
      offset: raw.offset ?? filters.offset ?? 0,
    };
  },
};
